import { useState, useEffect } from "react";
import { MdAdd } from "react-icons/md";
import {
  GenericTable,
  Pagination,
  Spinner,
    RoleValidator,
  SearchableDropdown,
} from "../../../Components/Common";
import { useGRN } from "../Hooks/useGRN";
import { useWarehouse } from "../../Inventory/Hooks/useWarehouse";
import { useMRR } from "../../Inventory/Hooks/useMRR";
import { toProperCase } from "../../../Utils";
import { useNavigate } from "react-router-dom";
import {
  GRN_LIST_COLUMNS,
  GRN_LIST_LABELS,
  GRN_MODES,
  getGRNStatusColor,
  GRN_STATUS_NAMES,
} from "./constant";
import { ROLES } from "../../../Constants/Strings";
import { Info, Edit, Trash2 } from "lucide-react";
import {
    CREATE_GRN,
  DETAIL_GRN,
  EDIT_GRN,
} from "../../../routes";

const GRNList = () => {
    const [grns, setGrns] = useState([]);
    const { fetchWarehouses } = useWarehouse();
    const { fetchAllMRRs } = useMRR();
    const navigate = useNavigate();

    const {
        loading,
        filters,
        setFilters,
        page,
        totalPages,
        rowsPerPage,
        onPageChange,
        onPageSizeChange,
        fetchGRNs,
        deleteGRNs,
    } = useGRN();

    const fetchData = async () => {
        const data = await fetchGRNs();
        setGrns(data || []);
    };

    useEffect(() => {
        fetchData();
    }, [page, rowsPerPage, filters]);

    const onDelete = async (id,status) => {
        if (status !== GRN_STATUS_NAMES.DRAFT) return;
        const confirmed = window.confirm("Are you sure you want to delete this GRN?");
        if (!confirmed) return;

        const success = await deleteGRNs(id);
        if (success) {
            const refreshed = await fetchGRNs(); 
            setGrns(refreshed || []);
        }
    };

    const handleAddGRN = () => navigate(CREATE_GRN);

    const handleGRNDetails = (id, mode, status) => {
        if (mode === GRN_MODES.VIEW) {
            navigate(DETAIL_GRN.replace(":instanceId", id));
        } else if (mode === GRN_MODES.EDIT) {
            if (status !== GRN_STATUS_NAMES.DRAFT) return; 
            navigate(EDIT_GRN.replace(":instanceId", id));
        }
    };


    // Mobile card rendering
    const renderMobileGRNFields = (grn) => (
        <div className="mb-2 relative p-2 border rounded-md bg-white">
            <div className="flex justify-between items-start">
                <div className="text-md font-bold text-gray-900 truncate">{grn.grn_code}</div>
                <div className="flex items-center gap-3">
                    <Info
                        className="h-5 w-5 text-blue-500 cursor-pointer hover:text-blue-700 transition-colors"
                        onClick={() => handleGRNDetails(grn.id, GRN_MODES.VIEW)}
                    />
                    {/* Edit Button */}
                    <RoleValidator
                        allowedRoles={[ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER]}
                        mode="disable"
                    >
                        <button
                            onClick={() =>
                                handleGRNDetails(grn.id, GRN_MODES.EDIT)
                            }
                            className={`h-5 w-5 cursor-pointer transition-colors ${grn.status === GRN_STATUS_NAMES.DRAFT
                                ? "text-blue-500 hover:text-blue-700"
                                : "text-gray-400 cursor-not-allowed"
                                }`}
                        >
                            <Edit size={18} />
                        </button>    
                    </RoleValidator>

                    {/* Delete Button */}
                    <RoleValidator
                        allowedRoles={[ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER]}
                        mode="disable"
                    >
                        <button onClick={() => {
                           onDelete(grn.id);
                        }}
                            className={`h-5 w-5 cursor-pointer transition-colors ${grn.status === GRN_STATUS_NAMES.DRAFT
                                ? "text-red-500 hover:text-red-700"
                                : "text-gray-400 cursor-not-allowed"
                                }`}
                        >
                            <Trash2 size={18} />
                        </button>
                    </RoleValidator>
                </div>
            </div>
            <div className="text-lg text-gray-900 truncate mt-1">{grn.warehouse_detail?.name || "-"}</div>
            <div className="text-sm text-gray-700 mt-1">
                MRR: {grn.mrr_detail?.mrr_code || "-"}
            </div>

            <span
                className={`absolute bottom-2 right-2 font-medium px-2 py-1 rounded-full text-xs md:text-sm ${getGRNStatusColor(grn.status)}`}
            >
                {grn.status ? toProperCase(grn.status) : "-"}
            </span>

        </div>
    );
    // Table field rendering
    const renderField = (key, row) => {
        switch (key) {
            case "name":
                return row.warehouse_detail?.name || "-";
            case "id":
                return row.grn_code;
            case "status":
                return <span className={`font-medium px-2 py-1 rounded-full text-xs ${getGRNStatusColor(row.status)}`}>
                    {row.status ? toProperCase(row.status) : "-"}
                </span>
            case "created":
                return new Date(row.created).toLocaleString();
            case "created_by":
                return row.created_by || "-";
            case "mrr":
                return row.mrr_detail?.mrr_code || "-";
            case "action":
                return (
                    <div className="flex items-center gap-3">
                        
                        <Info
                            className="h-5 w-5 text-blue-500 cursor-pointer hover:text-blue-700 transition-colors"
                            onClick={() =>
                                handleGRNDetails(row.id, GRN_MODES.VIEW)
                            }
                        />
                        {/* Edit Button */}
                        <RoleValidator
                            allowedRoles={[ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER]}
                            mode="disable"
                        >
                            <button
                                onClick={() =>
                                    handleGRNDetails(row.id, GRN_MODES.EDIT, row.status)
                                }
                                className={`h-5 w-5 cursor-pointer transition-colors ${row.status === GRN_STATUS_NAMES.DRAFT ?
                                    "text-blue-500 hover:text-blue-700" :
                                    "text-gray-400 cursor-not-allowed"}`}
                            >
                                <Edit size={18} />
                            </button>
                        </RoleValidator>

                        {/* Delete Button */}
                        <RoleValidator
                            allowedRoles={[ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER]}
                            mode="disable"
                        >
                            <button onClick={() => {
                                onDelete(row.id,row.status);
                            }}
                                className={`h-5 w-5 cursor-pointer transition-colors ${row.status === GRN_STATUS_NAMES.DRAFT
                                    ? "text-red-500 hover:text-red-700" :
                                    "text-gray-400 cursor-not-allowed"}`}
                            >
                                <Trash2 size={18} />
                            </button>
                        </RoleValidator>
                    </div>
                );
            default: return row[key] || "-";
        }
    };

    return (
        <div className="flex flex-col p-4 h-screen">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-bold">Goods Receive Notes (GRN)</h1>
                <RoleValidator
                    allowedRoles={[ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER]}
                    mode="disable"
                >
                    <button onClick={handleAddGRN} className="bg-primary text-white px-2 py-1 rounded-md flex items-center gap-2">
                    <MdAdd size={18} /><span className="whitespace-nowrap">Add GRN</span>
                    </button>
                </RoleValidator>
            </div>
            <div className="bg-white w-full rounded-2xl shadow-md border px-4 py-3">
                <div className="flex gap-2">
                    <SearchableDropdown
                        fetchFunction={(param) =>
                            fetchWarehouses({ ...param, is_site: true })
                        }
                    onSelect={(val) => setFilters({ ...filters, warehouse: val })}
                    valueKey="id"
                    labelKey="name"
                    includeAllOption
                    allOptionLabel="All Warehouses"
                    placeholder={"Select Warehouse"}
                    width="64"
                    />
                    <SearchableDropdown
                    fetchFunction={fetchAllMRRs}
                    onSelect={(val) => setFilters({ ...filters, mrr: val })}
                    valueKey="id"
                    labelKey="mrr_code"
                    includeAllOption
                    allOptionLabel="All MRRs"
                    placeholder={"Select MRR"}
                    width="64"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="w-full flex flex-col flex-1 px-1">
                {loading ? (
                    <div className="justify-center items-center w-full h-full"><Spinner /></div>
                ) : (
                    <div className="flex-1 overflow-y-auto px-1">
                        <GenericTable
                            tableColumns={GRN_LIST_COLUMNS}
                            tableRows={grns}
                            columnLabels={GRN_LIST_LABELS}
                            renderField={renderField}
                            enableGridLayout={true}
                            mobileGridColumns={1}
                            renderMobileCard={renderMobileGRNFields}
                        />
                        <div className="shrink-0 mt-2">
                            <Pagination
                                page={page}
                                rowsPerPage={rowsPerPage}
                                onPageChange={onPageChange}
                                onPageSizeChange={onPageSizeChange}
                                totalPages={totalPages}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GRNList;
